import { useEffect, useState } from 'react';
import { Placement } from '@floating-ui/react';
import { css, cx } from '@linaria/atomic';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { theme } from '@frontend/theme';
import { Button } from '../button/button.component';
import type { ButtonSize } from '../button/button.type';
import { PopoverDialog } from '../popover/dialog/popover-dialog.component';
import { usePopoverDialog } from '../popover/dialog/use-popover-dialog';

export const EmojiButton = ({
  onSelect,
  disabled,
  pickerWidth = 350,
  label = 'Add Emoji',
  popoverPlacement = 'bottom-start',
  isLoading = false,
  buttonSize = 'large',
  ...rest
}: {
  onSelect: (emoji: EmojiClickData) => void;
  disabled?: boolean;
  pickerWidth?: number;
  trackingId?: string;
  label?: string;
  showLabelAlways?: boolean;
  isLoading?: boolean;
  buttonSize?: ButtonSize;
  popoverPlacement?: Placement;
  className?: string;
}) => {
  const { getDialogProps, getTriggerProps, isOpen, close } = usePopoverDialog<HTMLButtonElement | HTMLAnchorElement>({
    placement: popoverPlacement,
    middlewareOptions: {
      flip: {
        crossAxis: true,
        padding: 8,
      },
      shift: {
        crossAxis: true,
        padding: 8,
      },
    },
  });
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  useEffect(() => {
    let id: ReturnType<typeof setTimeout>;

    if (!isOpen) {
      setShowEmojiPicker(false);
    }

    if (isOpen) {
      id = setTimeout(() => setShowEmojiPicker(true), 400);
    }
    return () => {
      if (id) {
        clearTimeout(id);
      }
    };
  }, [isOpen]);

  const handleSelect = (emoji: EmojiClickData) => {
    onSelect(emoji);
    setShowEmojiPicker(false);
    close();
  };

  const isDisabled = disabled || isLoading;

  return (
    <>
      <Button
        iconName='emoji'
        size={buttonSize}
        variant='secondary'
        {...getTriggerProps()}
        label={label}
        disabled={isDisabled}
        hoverLabel={label}
        loading={isLoading}
        {...rest}
      />
      <PopoverDialog
        {...getDialogProps()}
        className={popoverDialogStyle}
        css={{ width: pickerWidth }}
        returnFocus={false}
      >
        <div className={emojiPickerBoxStyle} style={{ width: pickerWidth }}>
          {showEmojiPicker ? (
            <EmojiPicker onEmojiClick={handleSelect} lazyLoadEmojis height={450} width={pickerWidth} />
          ) : (
            <TypingIndicator />
          )}
        </div>
      </PopoverDialog>
    </>
  );
};

const popoverDialogStyle = css`
  max-height: 450px;
  padding: 0;
  border-radius: 8px;
  max-width: calc(100vw - ${theme.spacing(2)});
`;

const emojiPickerBoxStyle = css`
  height: 450px;
  display: flex;
  justify-content: center;
  max-width: calc(100vw - ${theme.spacing(2)});
  align-items: center;

  * {
    font-family: ${theme.font.family};
  }
`;

const dotStyle = css`
  width: 4px;
  height: 4px;
  background-color: ${theme.colors.neutral50};
  border-radius: ${theme.borderRadius.small};
  display: inline-block;
  margin: ${theme.spacing(0, 0.25)};
`;

const typingIndicatorStyle = css`
  margin-left: ${theme.spacing(1)};
  margin-top: ${theme.spacing(1)};

  &::before,
  &::after {
    content: '';
    width: 4px;
    height: 4px;
    background-color: ${theme.colors.neutral50};
    border-radius: ${theme.borderRadius.small};
    display: inline-block;
    margin: ${theme.spacing(0, 0.25)};
  }

  &::before {
    animation: emoji-blink 1.5s ease infinite 0.5s;
  }

  &::after {
    animation: emoji-blink 1.5s ease infinite 1.5s;
  }

  @keyframes emoji-blink {
    0%,
    100% {
      opacity: 0.3;
    }
    50% {
      opacity: 1;
      transform: scale(1.3);
    }
  }
`;

const dotBlinkStyle = css`
  animation: emoji-blink 1.5s ease infinite 1s;
`;

export const TypingIndicator = () => (
  <span className={typingIndicatorStyle} aria-hidden='true'>
    <span className={cx(dotStyle, dotBlinkStyle)} />
  </span>
);
